const MASSIVE_PERIOD_TRACE_DB_NAME = 'FrontLucaVinculaciones'
const MASSIVE_PERIOD_TRACE_DB_VERSION = 1
const MASSIVE_PERIOD_TRACE_STORE_NAME = 'massivePeriodTraces'
const MASSIVE_PERIOD_TRACE_LEGACY_STORAGE_KEY = 'vinculaciones.massivePeriodTraces'

const isIndexedDBAvailable = () =>
  typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined'

const readLegacyMassivePeriodTraceMap = () => {
  try {
    const raw = localStorage.getItem(MASSIVE_PERIOD_TRACE_LEGACY_STORAGE_KEY)
    if (!raw) return {}

    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch (error) {
    console.error('Error al leer trazas masivas antiguas desde localStorage:', error)
    return {}
  }
}

const getLegacyMassivePeriodTraces = businessId => {
  if (!businessId) return []

  const storedMap = readLegacyMassivePeriodTraceMap()
  const traces = storedMap[String(businessId)]

  return Array.isArray(traces) ? traces : []
}

const openMassivePeriodTraceDatabase = () =>
  new Promise((resolve, reject) => {
    if (!isIndexedDBAvailable()) {
      reject(new Error('IndexedDB no esta disponible en este navegador'))
      return
    }

    const request = window.indexedDB.open(
      MASSIVE_PERIOD_TRACE_DB_NAME,
      MASSIVE_PERIOD_TRACE_DB_VERSION
    )

    request.onupgradeneeded = event => {
      const database = event?.target?.result
      if (!database.objectStoreNames.contains(MASSIVE_PERIOD_TRACE_STORE_NAME)) {
        database.createObjectStore(MASSIVE_PERIOD_TRACE_STORE_NAME, { keyPath: 'businessId' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () =>
      reject(request.error || new Error('No se pudo abrir IndexedDB para trazas masivas'))
  })

const runMassivePeriodTraceStoreRequest = async (mode, requestFactory) => {
  const database = await openMassivePeriodTraceDatabase()

  return new Promise((resolve, reject) => {
    const transaction = database.transaction(MASSIVE_PERIOD_TRACE_STORE_NAME, mode)
    const store = transaction.objectStore(MASSIVE_PERIOD_TRACE_STORE_NAME)
    let request

    try {
      request = requestFactory(store)
    } catch (error) {
      database.close()
      reject(error)
      return
    }

    transaction.oncomplete = () => {
      const result = request?.result
      database.close()
      resolve(result)
    }

    transaction.onerror = () => {
      const transactionError =
        transaction.error || request?.error || new Error('Error en IndexedDB para trazas masivas')
      database.close()
      reject(transactionError)
    }

    transaction.onabort = () => {
      database.close()
      reject(transaction.error || new Error('La transaccion de IndexedDB fue abortada'))
    }
  })
}

export const getStoredMassivePeriodTraces = async businessId => {
  if (!businessId) return []

  if (!isIndexedDBAvailable()) {
    return getLegacyMassivePeriodTraces(businessId)
  }

  try {
    const record = await runMassivePeriodTraceStoreRequest('readonly', store =>
      store.get(String(businessId))
    )

    return Array.isArray(record?.traces) ? record.traces : []
  } catch (error) {
    console.error('Error al leer trazas masivas desde IndexedDB:', error)
    return []
  }
}

export const persistMassivePeriodTraces = async (businessId, traces) => {
  if (!businessId) return

  const normalizedTraces = Array.isArray(traces) ? traces : []

  if (!isIndexedDBAvailable()) {
    console.warn('IndexedDB no esta disponible; no se pudo persistir trazas masivas fuera de localStorage.clear().')
    return
  }

  try {
    await runMassivePeriodTraceStoreRequest('readwrite', store =>
      store.put({
        businessId: String(businessId),
        traces: normalizedTraces,
        updatedAt: new Date().toISOString()
      })
    )
  } catch (error) {
    console.error('Error al guardar trazas masivas en IndexedDB:', error)
  }
}

export const migrateLegacyMassivePeriodTraces = async businessId => {
  if (!businessId) return []

  if (!isIndexedDBAvailable()) {
    return getLegacyMassivePeriodTraces(businessId)
  }

  const indexedDBTraces = await getStoredMassivePeriodTraces(businessId)
  if (indexedDBTraces.length > 0) return indexedDBTraces

  const legacyTraces = getLegacyMassivePeriodTraces(businessId)
  if (legacyTraces.length === 0) return []

  await persistMassivePeriodTraces(businessId, legacyTraces)
  return legacyTraces
}
